import React, { useEffect, useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { addToLocalCart } from "../services/CartServices";
import AOS from "aos";
import "aos/dist/aos.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { motion, AnimatePresence } from "framer-motion";
import Tilt from "react-parallax-tilt";
import { QuickViewModal } from "../components/QuickViewModal";
import "./ProductPage.css";

const ProductPage = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [sortOrder, setSortOrder] = useState("asc");
  const [wishlist, setWishlist] = useState([]);
  const [quickProduct, setQuickProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/products`);
      setProducts(res.data);
    } catch (err) {
      toast.error("Failed to load products");
    }
  };

  // open quick view
  const openQuickView = (product) => {
    setQuickProduct(product);
    setIsModalOpen(true);
  };

  // Use discountedPrice if available
  const handleAddToCart = (product) => {
    const price = product.discountedPrice ? Number(product.discountedPrice) : Number(product.price);
    addToLocalCart({ ...product, price });
    toast.success(`${product.name} added to cart!`);
  };

  const handleWishlistToggle = (productId) => {
    if (wishlist.includes(productId)) {
      setWishlist(wishlist.filter((id) => id !== productId));
      toast("Removed from wishlist");
    } else {
      setWishlist([...wishlist, productId]);
      toast.success("Added to wishlist");
    }
  };

  const filteredProducts = products
    .filter((product) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) =>
      sortOrder === "asc"
        ? (a.discountedPrice ? a.discountedPrice : a.price) - (b.discountedPrice ? b.discountedPrice : b.price)
        : (b.discountedPrice ? b.discountedPrice : b.price) - (a.discountedPrice ? a.discountedPrice : a.price)
    );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  return (
    <div className="product-page container-fluid py-5">
      <Toaster position="top-right" />
      {/* Search & Sort */}
      <div className="d-flex flex-wrap  justify-content-between align-items-center mb-4 gap-3">
        <motion.h2
          className="section-title text-dark"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          ✨ Our Premium Collection
        </motion.h2>
        <div className="d-flex gap-2">
          <input
            type="text"
            placeholder="Search..."
            className="form-control rounded-pill shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="form-select rounded-pill shadow-sm"
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="asc">Price: Low → High</option>
            <option value="desc">Price: High → Low</option>
          </select>
        </div>
      </div>

      {/* Product Cards */}
      <motion.div
        className="row g-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <AnimatePresence>
          {currentProducts.map((product, i) => (
            <motion.div
              className="col-md-4"
              key={product.id}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Tilt
                tiltMaxAngleX={15}
                tiltMaxAngleY={15}
                scale={1.05}
                transitionSpeed={400}
              >
                {/* Updated Card */}
                <motion.div
                  className="card product-card border-0 shadow-lg overflow-hidden"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="product-img"
                  />

                  {/* Wishlist Heart */}
                  <motion.i
                    className={`wishlist-icon bi ${wishlist.includes(product.id)
                        ? "bi-heart-fill text-danger"
                        : "bi-heart"
                      }`}
                    onClick={() => handleWishlistToggle(product.id)}
                    whileTap={{ scale: 0.8 }}
                  />

                  {/* Overlay Content */}
                  <div className="product-overlay">
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <span className="btn rounded-pill" style={{ color: "white", backgroundColor: "rgba(0, 0, 0, 0.7)", pointerEvents: "none" }}>
                        ₹{product.discountedPrice ? product.discountedPrice : product.price}
                        {product.discountPercent > 0 && (
                          <span style={{ color: "red", marginLeft: "8px", fontSize: "0.95em" }}>
                            <del>₹{product.price}</del> ({product.discountPercent}% OFF)
                          </span>
                        )}
                      </span>

                      <motion.button

                        className="btn btn-info rounded-pill px-3"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openQuickView(product)}
                      >
                        <i className="bi bi-eye bg-transparent "></i> Quick View
                      </motion.button>

                      <motion.button
                        className="btn btn-success rounded-pill px-3"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleAddToCart(product)}
                      >
                        <i className="bi bi-cart-plus bg-transparent"></i> Add to Cart
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </Tilt>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Pagination */}
      <div className="d-flex justify-content-center mt-5">
        <ul className="pagination pagination-lg gap-2">
          {Array.from({ length: totalPages }, (_, i) => (
            <li
              key={i}
              className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
            >
              <button
                className="page-link rounded-pill shadow-sm"
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Quick View Modal */}
      <QuickViewModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        product={quickProduct} 
      />
    </div>
  );
};

export default ProductPage;
